import getCities from "@component_path/lib/call_api/master_data/cities";

const FOREIGN_COUNTRY_ID = 99;

/**
 * データ構造
 * @return {
 *  employment_types: Array<{ value: string, label: string }>,
 *  cities: Array<{ value: string, label: string }>,
 *  prefectures: Array<{ value: string, label: string }>,
 *  position_types: Array<{ value: string, label: string }>,
 *  wishful_times: Array<{ value: string, label: string }>,
 *  birth_years: Array<{ value: string, label: string }>,
 *  work_statuses: Array<{ value: string, label: string }>,
 *  sexes: Array<{ value: string, label: string }>,
 *  qualifieds: Array<{ value: string, label: string }>,
 *  work_experiences: Array<{ value: string, label: string }>,
 *  experience_years: Array<{ value: string, label: string }>,
 *  form_param_keys: Array<string>,
 *  is_logged_in: boolean
 * }
 */
const state = {
  employment_types: [],
  cities: [],
  prefectures: [],
  position_types: [],
  wishful_times: [],
  birth_years: [],
  work_statuses: [],
  sexes: [],
  experience_years: [],
  qualifieds: [],
  work_experiences: [],
  form_param_key: undefined,
  post_item_keys: [],
  is_logged_in: false,
  // ASプレミアムLP_スライド用 転職支援会社
  agentSelectionAuctions: []
};

/**
 * 選択肢になるデータは下記のデータ構造に整形する
 * @return { value: string, label: string }
 *          value: 選択肢の値
 *          label: 画面上に表示される選択肢の内容
 */
const actions = {
  initData({ dispatch }, master_data) {
    dispatch("setEmploymentTypes", master_data.employment_types || []);
    dispatch("setPositionTypes", master_data.position_types || []);
    dispatch("setPrefectures", master_data.prefectures || []);
    dispatch("setWishfulTimes", master_data.wishful_times || []);
    dispatch("setBirthYears", master_data.birth_years || []);
    dispatch("setWorkStatuses", master_data.work_statuses || []);
    dispatch("setSexes", master_data.sexes || []);
    dispatch("setQualifieds", master_data.qualifieds || []);
    dispatch("setWorkExperiences", master_data.work_experiences || []);
    dispatch("setExperienceYears", master_data.experience_years || []);
    dispatch("setFormParamKey", master_data.form_param_key);
    dispatch("setPostItemKeys", master_data.post_item_keys);
    dispatch("setIsLoggedIn", master_data.is_logged_in);
  },
  /**
   * employment_typesマスターデータの構造
   * @param Array<{ code: string, id: string, name: string }>
   *        code: DB上に保存される値(e.g. 1)
   *        id:   htmlに配置する用の名前?(e.g. lp_inquiry_employment_type_1)
   *        name: employment_typeを日本語化したもの(e.g. 正社員)
   */
  setEmploymentTypes({ commit }, employment_types) {
    const formattedEmploymentTypes = employment_types.map(employment_type => {
      return { value: employment_type.code, label: employment_type.name };
    });
    commit("SET_EMPLOYMENT_TYPES", formattedEmploymentTypes);
  },
  /**
   * position_typesマスターデータの構造
   * @param Array<{ code: string, id: string, name: string }>
   *        code: DB上に保存される値(e.g. HL)
   *        id:   htmlに配置する用の名前?(e.g. position_type_code_HL)
   *        name: position_typeを日本語化したもの(e.g. 病院)
   */
  setPositionTypes({ commit }, position_types) {
    const formattedPositionTypes = position_types.map(position_type => {
      return { value: position_type.code, label: position_type.name };
    });
    commit("SET_POSITION_TYPES", formattedPositionTypes);
  },
  /**
   * prefecturesマスターデータの構造
   * @param Array<{ area_id: number, id: number, mailmag_area_id: number, name: string,
   *                name_english: string, name_reading: string, neighbor1: number, neighbor2: number,
   *                neighbor3: number, short_name: number }>
   *        area_id:          関連する地方id(e.g. 1)
   *        id:               都道府県id(e.g. 1)
   *        mailmag_area_id:  メルマガ配信時に関連する地方id(e.g. 1)
   *        name:             名称(e.g. 北海道)
   *        name_english:     英名(e.g. hokkaido)
   *        name_reading:     ひらがな称(e.g. ほっかいどう)
   *        neighbor[1,2,3]   隣接する都道府県id(e.g. 1)
   *        short_name:       略称(e.g. 北海道)
   */
  setPrefectures({ commit }, prefectures) {
    const formattedPrefectures = prefectures.map(prefecture => {
      return { value: prefecture.id, label: prefecture.name };
    });
    commit("SET_PREFECTURES", formattedPrefectures);
  },
  /**
   * citiesマスターデータの構造
   * @param { key: number: { id: number, name: string, kind: string } }
   *        id:               都道府県id(e.g. 1)
   *        name:             名称(e.g. 北海道)
   *        kind:             データの種類(e.g. city)
   */
  setCities({ commit }, cities) {
    const formattedCities = Object.keys(cities).map(key => {
      const city = cities[key];
      return { value: city.id, label: city.name };
    });
    commit("SET_CITIES", formattedCities);
  },
  /**
   * wishful_timesマスターデータの構造
   * @param Array<{ code: string, id: string, name: string }>
   *        code: DB上に保存される値(e.g. 0)
   *        id:   htmlに配置する用の名前?(e.g. wishful_time_0)
   *        name: position_typeを日本語化したもの(e.g. よい求人があればいつでも)
   */
  setWishfulTimes({ commit }, wishful_times) {
    const formattedWishfulTimes = wishful_times.map(wishful_time => {
      return { value: wishful_time.code, label: wishful_time.name };
    });
    commit("SET_WISHFUL_TIMES", formattedWishfulTimes);
  },
  /**
   * birth_yearsマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 20)
   *        text:  ageを何年生まれに変換し、日本語として表示したもの(e.g. 平成4年);
   */
  setBirthYears({ commit }, birth_years) {
    const formattedBirthYears = birth_years.map(birth_year => {
      return { value: birth_year.value, label: birth_year.text };
    });
    commit("SET_BIRTH_YEARS", formattedBirthYears);
  },
  /**
   * work_statusesマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  在籍中 or 離職中;
   */
  setWorkStatuses({ commit }, work_statuses) {
    const formattedWorkStatuses = work_statuses.map(work_status => {
      return { value: work_status.value, label: work_status.text };
    });
    commit("SET_WORK_STATUSES", formattedWorkStatuses);
  },
  /**
   * sexesマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  男性 or 女性;
   */
  setSexes({ commit }, sexes) {
    const formattedSexes = sexes.map(sex => {
      return { value: sex.value, label: sex.text };
    });
    commit("SET_SEXES", formattedSexes);
  },
  /**
   * experience_yearsマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DBに保存される値(e.g. 0..50)
   *        text:  ~年 (or 1年未満);
   */
  setExperienceYears({ commit }, experience_years) {
    const formattedExperienceYears = experience_years.map(experience_year => {
      return { value: experience_year.value, label: experience_year.text };
    });
    commit("SET_EXPERIENCE_YEARS", formattedExperienceYears);
  },
  /**
   * qualifiedsマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  文字列;
   */
  setQualifieds({ commit }, qualifieds) {
    const formattedQualifieds = qualifieds.map(qualified => {
      return { value: qualified.value, label: qualified.text };
    });
    commit("SET_QUALIFIEDS", formattedQualifieds);
  },
  /**
   * work_experiencesマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  文字列;
   */
  setWorkExperiences({ commit }, work_experiences) {
    const formattedWorkExperiences = work_experiences.map(work_experience => {
      return { value: work_experience.value, label: work_experience.text };
    });
    commit("SET_WORK_EXPERIENCES", formattedWorkExperiences);
  },
  /**
   * sexesマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  男性 or 女性;
   */
  setSexes({ commit }, sexes) {
    const formattedSexes = sexes.map(sex => {
      return { value: sex.value, label: sex.text };
    });
    commit("SET_SEXES", formattedSexes);
  },
  /**
   * qualifiedsマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  文字列;
   */
  setQualifieds({ commit }, qualifieds) {
    const formattedQualifieds = qualifieds.map(qualified => {
      return { value: qualified.value, label: qualified.text };
    });
    commit("SET_QUALIFIEDS", formattedQualifieds);
  },
  /**
   * work_experiencesマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  文字列;
   */
  setWorkExperiences({ commit }, work_experiences) {
    const formattedWorkExperiences = work_experiences.map(work_experience => {
      return { value: work_experience.value, label: work_experience.text };
    });
    commit("SET_WORK_EXPERIENCES", formattedWorkExperiences);
  },
  /**
   * sexesマスターデータの構造
   * @param Array<{ value: number, text: string }>
   *        value: DB上に保存される値(e.g. 1 or 2)
   *        text:  男性 or 女性;
   */
  setSexes({ commit }, sexes) {
    const formattedSexes = sexes.map(sex => {
      return { value: sex.value, label: sex.text };
    });
    commit("SET_SEXES", formattedSexes);
  },
  /**
   * form_param_keyマスターデータの構造
   * @param string 問い合わせに必要な入力項目
   */
  setFormParamKey({ commit }, form_param_key) {
    commit("SET_FORM_PARAM_KEY", form_param_key);
  },
  /**
   * post_item_keysマスターデータの構造
   * @param Array<string> 問い合わせに必要な入力項目
   */
  setPostItemKeys({ commit }, post_item_keys) {
    commit("SET_POST_ITEM_KEYS", post_item_keys);
  },
  setIsLoggedIn({ commit }, is_logged_in) {
    commit("SET_IS_LOGGED_IN", is_logged_in);
  },
  async fetchCitiesFromExternal({ dispatch }, prefectureId) {
    // TODO 以下のように直して,呼び出し元でのif文を消す（ここで直しても動作確認できないので、別対応）
    // if (prefectureId) {
    //   await getCities(prefectureId, cities => dispatch("setCities", cities));
    // } else {
    //   dispatch("setCities", []);
    // }

    await getCities(prefectureId, cities => {
      dispatch("setCities", cities);
    });
  },
  setAgentSelectionAuctions({ commit }, value) {
    commit("SET_AGENT_SELECTION_AUCTIONS", value);
  }
};

const getters = {
  // @param [String] positionTypeCodes: 業種コードの配列
  // @return [PositionType or null]
  getPositionTypes: state => positionTypeCodes =>
    positionTypeCodes.map(code =>
      state.position_types.find(positionType => positionType.value == code)
    ),
  // @param String employmentTypeCode
  // @return EmploymentType or null
  getEmploymentType: state => employmentTypeCode =>
    state.employment_types.find(
      employmentType => employmentType.value == employmentTypeCode
    ),
  // @param Int prefectureId: 都道府県ID
  // @return Prefecture or null
  getPrefecture: state => prefectureId => {
    // 海外の場合、リストの一番最後に入ることになっている
    if (prefectureId == FOREIGN_COUNTRY_ID)
      return state.prefectures[state.prefectures.length - 1].text;

    // idとindexの都合上、-1が必要
    return state.prefectures[prefectureId - 1];
  }
};

const mutations = {
  SET_EMPLOYMENT_TYPES(state, employment_types) {
    state.employment_types = employment_types;
  },
  SET_POSITION_TYPES(state, position_types) {
    state.position_types = position_types;
  },
  SET_PREFECTURES(state, prefectures) {
    state.prefectures = prefectures;
  },
  SET_CITIES(state, cities) {
    state.cities = cities;
  },
  SET_WISHFUL_TIMES(state, wishful_times) {
    state.wishful_times = wishful_times;
  },
  SET_BIRTH_YEARS(state, birth_years) {
    state.birth_years = birth_years;
  },
  SET_WORK_STATUSES(state, work_statuses) {
    state.work_statuses = work_statuses;
  },
  SET_SEXES(state, sexes) {
    state.sexes = sexes;
  },
  SET_QUALIFIEDS(state, qualifieds) {
    state.qualifieds = qualifieds;
  },
  SET_WORK_EXPERIENCES(state, work_experiences) {
    state.work_experiences = work_experiences;
  },
  SET_EXPERIENCE_YEARS(state, experience_years) {
    state.experience_years = experience_years;
  },
  SET_FORM_PARAM_KEY(state, form_param_key) {
    state.form_param_key = form_param_key;
  },
  SET_POST_ITEM_KEYS(state, post_item_keys) {
    state.post_item_keys = post_item_keys;
  },
  SET_IS_LOGGED_IN(state, is_logged_in) {
    state.is_logged_in = is_logged_in;
  },
  SET_AGENT_SELECTION_AUCTIONS(state, value) {
    state.agentSelectionAuctions = value;
  }
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  actions,
  mutations
};
